import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import { PricebookPeriod } from "../../../../types/price-book.type";
import { formatNumber } from "../../../../utils";
import NameWithCode from "../../shared/NameWithCode";
import { CATALOGUE_CELL_PADDING, STRIPE_BACKGROUND } from "../../shared/constants";
import { formatDiscountPercent } from "../constants";
import PeriodRangeText from "../Periods/PeriodRangeText";
import PeriodStatusChip from "../Periods/PeriodStatusChip";
import { isPeriodEnded } from "../Periods/periodModel";

/** Shown where a period keeps its line's unit price or %. */
const FOLLOWS_LINE_LABEL = "ตามรายการ";
const NO_VALUE = "-";

interface PeriodRowProps {
  period: PricebookPeriod;
  striped: boolean;
  /** Shows the action column (a writer, on a book that takes periods). */
  canManage: boolean;
  /** Why the actions are shut right now; empty when they are open. */
  blocker: string;
  onEdit: (period: PricebookPeriod) => void;
  onExtend: (period: PricebookPeriod) => void;
  onDelete: (period: PricebookPeriod) => void;
}

/** One period of the ช่วงราคา tab. An ended period is a read-only record. */
export default function PeriodRow({
  period,
  striped,
  canManage,
  blocker,
  onEdit,
  onExtend,
  onDelete,
}: PeriodRowProps) {
  const isEnded = isPeriodEnded(period.status);
  const isLocked = Boolean(blocker);
  const unitPriceText =
    period.unit_price === null ? FOLLOWS_LINE_LABEL : formatNumber(period.unit_price);
  const discountText =
    period.discount_percent === null
      ? FOLLOWS_LINE_LABEL
      : formatDiscountPercent(period.discount_percent);

  const handleEdit = () => onEdit(period);
  const handleExtend = () => onExtend(period);
  const handleDelete = () => onDelete(period);

  return (
    <TableRow
      sx={{
        bgcolor: (theme) =>
          striped ? STRIPE_BACKGROUND : theme.palette.background.paper,
        "& td": { padding: `${CATALOGUE_CELL_PADDING}px` },
      }}
    >
      <TableCell>
        <NameWithCode name={period.product_name} code={period.product_code} />
      </TableCell>
      <TableCell align="center">
        <PeriodRangeText period={period} />
      </TableCell>
      <TableCell align="center">{unitPriceText}</TableCell>
      <TableCell align="center">{discountText}</TableCell>
      <TableCell align="center">{formatNumber(period.price_incl_vat)}</TableCell>
      <TableCell align="center">{formatNumber(period.price_ex_vat)}</TableCell>
      <TableCell>{period.note ?? NO_VALUE}</TableCell>
      <TableCell align="center">
        <PeriodStatusChip status={period.status} />
      </TableCell>
      {canManage && (
        <TableCell align="center">
          {!isEnded && (
            <Tooltip title={blocker}>
              <Stack direction="row" justifyContent="center" gap={0.5}>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`แก้ไขช่วงราคา ${period.product_code}`}
                  disabled={isLocked}
                  onClick={handleEdit}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`ต่อช่วงราคา ${period.product_code}`}
                  disabled={isLocked}
                  onClick={handleExtend}
                >
                  <MoreTimeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`ลบช่วงราคา ${period.product_code}`}
                  disabled={isLocked}
                  onClick={handleDelete}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
